import { useState } from 'react';
import { Link } from 'react-router-dom';
import { venueUrl } from '@/lib/urls';
import { motion } from 'framer-motion';
import { 
  ChevronLeft, 
  ChevronRight, 
  MapPin, 
  Calendar, 
  Star, 
  Clock, 
  Users, 
  Ticket,
  ArrowRight
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { formatDateShort, formatTime } from '@/lib/dateUtils';

const ImageCarousel = ({ images, alt }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  
  const placeholderImages = [
    'https://images.unsplash.com/photo-1642721085217-b5f5b6b47d0a?crop=entropy&cs=srgb&fm=jpg&q=85',
    'https://images.unsplash.com/photo-1581378670696-6bb27d6e450b?crop=entropy&cs=srgb&fm=jpg&q=85'
  ];
  
  const displayImages = images && images.length > 0 ? images : placeholderImages;
  
  const nextImage = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentIndex((prev) => (prev + 1) % displayImages.length);
  };
  
  const prevImage = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentIndex((prev) => (prev - 1 + displayImages.length) % displayImages.length);
  };

  return (
    <div className="relative w-full h-full group/carousel">
      <img
        src={displayImages[currentIndex]}
        alt={`${alt} - Image ${currentIndex + 1}`}
        className="w-full h-full object-cover"
      />
      
      {displayImages.length > 1 && (
        <>
          <button
            onClick={prevImage}
            className="absolute left-3 top-1/2 -translate-y-1/2 w-8 h-8 bg-black/60 hover:bg-black/80 rounded-full flex items-center justify-center text-white opacity-0 group-hover/carousel:opacity-100 transition-opacity"
            aria-label="Previous image"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            onClick={nextImage}
            className="absolute right-3 top-1/2 -translate-y-1/2 w-8 h-8 bg-black/60 hover:bg-black/80 rounded-full flex items-center justify-center text-white opacity-0 group-hover/carousel:opacity-100 transition-opacity"
            aria-label="Next image"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
          
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
            {displayImages.map((_, idx) => (
              <button
                key={idx}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setCurrentIndex(idx);
                }}
                className={`w-2 h-2 rounded-full transition-all ${
                  idx === currentIndex 
                    ? 'bg-white w-4' 
                    : 'bg-white/50 hover:bg-white/75'
                }`}
                aria-label={`Go to image ${idx + 1}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export const EventGuideCard = ({ event, index = 0 }) => {
  const availableTickets = event.capacity - (event.tickets_sold || 0);
  const isSellingFast = availableTickets < event.capacity * 0.2;
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.08 }}
      data-testid={`event-guide-card-${event.id}`}
    >
      <Link to={`/events/${event.id}`}>
        <Card className="overflow-hidden border border-border/50 hover:border-indigo-500/50 transition-all duration-300 group bg-card hover:shadow-xl">
          <div className="flex flex-col lg:flex-row">
            <div className="lg:w-2/5 relative aspect-[16/10] lg:aspect-auto overflow-hidden">
              <ImageCarousel images={event.images} alt={event.title} />
              
              <div className="absolute top-3 left-3 flex flex-wrap gap-2">
                {event.gold_exclusive && (
                  <Badge className="bg-[#D4AF37] text-black font-bold border-0">
                    <Star className="w-3 h-3 mr-1 fill-current" />
                    GOLD EXCLUSIVE
                  </Badge>
                )}
                {isSellingFast && (
                  <Badge className="bg-rose-500 text-white font-bold border-0">
                    <Ticket className="w-3 h-3 mr-1" />
                    SELLING FAST
                  </Badge>
                )}
              </div>
            </div>
            
            <CardContent className="lg:w-3/5 p-6 lg:p-8 flex flex-col justify-between">
              <div>
                <div className="flex items-start justify-between gap-4 mb-3">
                  <div>
                    <p className="text-sm font-medium text-indigo-600 dark:text-indigo-400 mb-1">
                      {event.category?.replace('_', ' ').toUpperCase() || 'EVENT'}
                    </p>
                    <h3 className="text-xl lg:text-2xl font-bold text-foreground group-hover:text-indigo-600 transition-colors line-clamp-2">
                      {event.title}
                    </h3>
                  </div>
                </div>
                
                <p className="text-muted-foreground mb-4 line-clamp-2 lg:line-clamp-3">
                  {event.description || 'Join us for an unforgettable experience in the heart of RiNo Art District.'}
                </p>
                
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <MapPin className="w-4 h-4 text-indigo-500 flex-shrink-0" />
                    <span className="truncate">{event.venue_name}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Calendar className="w-4 h-4 text-indigo-500 flex-shrink-0" />
                    <span>{formatDateShort(event.date)}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Clock className="w-4 h-4 text-indigo-500 flex-shrink-0" />
                    <span>{formatTime(event.time) || '7:00 PM'}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Users className="w-4 h-4 text-indigo-500 flex-shrink-0" />
                    <span>{availableTickets} spots left</span>
                  </div>
                </div>
              </div>
              
              <div className="flex items-center justify-between pt-4 border-t border-border/50">
                <div>
                  <p className="text-xs text-muted-foreground mb-1">From</p>
                  <div className="flex items-baseline gap-2">
                    <span className="text-2xl font-bold text-foreground">
                      ${event.price?.toFixed(0) || '0'}
                    </span>
                    {event.gold_price && (
                      <span className="text-sm text-[#D4AF37] font-medium">
                        ${event.gold_price.toFixed(0)} Gold
                      </span>
                    )}
                  </div>
                </div>
                <Button className="bg-indigo-600 hover:bg-indigo-700 text-white gap-2 group/btn">
                  View Details
                  <ArrowRight className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
                </Button>
              </div>
            </CardContent>
          </div>
        </Card>
      </Link>
    </motion.div>
  );
};

export const VenueGuideCard = ({ venue, index = 0, reviewStats }) => {
  const avgRating = reviewStats?.avgRating || 4.5;
  const reviewCount = reviewStats?.count || 0;
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.08 }}
      data-testid={`venue-guide-card-${venue.id}`}
    >
      <Link to={venueUrl(venue)}>
        <Card className="overflow-hidden border border-border/50 hover:border-indigo-500/50 transition-all duration-300 group bg-card hover:shadow-xl">
          <div className="flex flex-col lg:flex-row">
            <div className="lg:w-2/5 relative aspect-[16/10] lg:aspect-auto overflow-hidden">
              <ImageCarousel images={venue.images} alt={venue.name} />
              
              <div className="absolute top-3 left-3">
                <Badge className="bg-yellow-400/90 text-black font-bold border-0">
                  {venue.district}
                </Badge>
              </div>
              
              {reviewCount > 0 && (
                <div className="absolute bottom-3 left-3 bg-black/70 backdrop-blur-sm text-white px-3 py-1.5 rounded-full flex items-center gap-1.5">
                  <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                  <span className="font-bold">{avgRating.toFixed(1)}</span>
                  <span className="text-white/70 text-sm">({reviewCount})</span>
                </div>
              )}
            </div>
            
            <CardContent className="lg:w-3/5 p-6 lg:p-8 flex flex-col justify-between">
              <div>
                <div className="mb-3">
                  <p className="text-sm font-medium text-indigo-600 dark:text-indigo-400 mb-1">
                    PARTNER VENUE
                  </p>
                  <h3 className="text-xl lg:text-2xl font-bold text-foreground group-hover:text-indigo-600 transition-colors">
                    {venue.name}
                  </h3>
                </div>
                
                <p className="text-muted-foreground mb-4 line-clamp-3">
                  {venue.description}
                </p>
                
                <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
                  <MapPin className="w-4 h-4 text-indigo-500 flex-shrink-0" />
                  <span>{venue.address}</span>
                </div>
                
                {venue.amenities && venue.amenities.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-4">
                    {venue.amenities.slice(0, 4).map((amenity, idx) => (
                      <Badge key={idx} variant="secondary" className="text-xs">
                        {amenity}
                      </Badge>
                    ))}
                    {venue.amenities.length > 4 && (
                      <Badge variant="outline" className="text-xs">
                        +{venue.amenities.length - 4} more
                      </Badge>
                    )}
                  </div>
                )}
              </div>
              
              <div className="flex items-center justify-between pt-4 border-t border-border/50">
                <div className="text-sm text-muted-foreground">
                  <span className="text-foreground font-medium">Explore events, </span>
                  experiences & private hire
                </div>
                <Button className="bg-indigo-600 hover:bg-indigo-700 text-white gap-2 group/btn">
                  View Venue
                  <ArrowRight className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
                </Button>
              </div>
            </CardContent>
          </div>
        </Card>
      </Link>
    </motion.div>
  );
};

export const CompactEventCard = ({ event, index = 0 }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
      whileHover={{ y: -4 }}
      data-testid={`compact-event-card-${event.id}`}
    >
      <Link to={`/events/${event.id}`}>
        <Card className="overflow-hidden border border-border/50 hover:border-indigo-500/50 transition-all cursor-pointer group h-full">
          <div className="aspect-[4/3] bg-gradient-to-br from-indigo-100 to-purple-100 dark:from-indigo-900/20 dark:to-purple-900/20 relative overflow-hidden">
            {event.images && event.images[0] ? (
              <img
                src={event.images[0]}
                alt={event.title}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <Calendar className="w-12 h-12 text-indigo-400" />
              </div>
            )}
            {event.gold_exclusive && (
              <Badge className="absolute top-3 right-3 bg-[#D4AF37] text-black font-bold border-0">
                <Star className="w-3 h-3 mr-1 fill-current" />
                GOLD
              </Badge>
            )}
          </div>
          <CardContent className="p-4">
            <p className="text-xs font-medium text-indigo-600 dark:text-indigo-400 mb-1">
              {event.category?.replace('_', ' ').toUpperCase() || 'EVENT'}
            </p>
            <h3 className="font-bold text-foreground mb-2 line-clamp-2 group-hover:text-indigo-600 transition-colors">
              {event.title}
            </h3>
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
              <MapPin className="w-3 h-3" />
              <span className="truncate">{event.venue_name}</span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Calendar className="w-3 h-3" />
                {formatDateShort(event.date)}
              </div>
              <span className="font-bold text-foreground">${event.price?.toFixed(0)}</span>
            </div>
          </CardContent>
        </Card>
      </Link>
    </motion.div>
  );
};
